import React, { createContext, useContext, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { CALENDAR_CONSTANTS, VIEW_MODES } from '../utils/calendar/constants';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const CalendarContext = createContext();

export const useCalendar = () => {
    const context = useContext(CalendarContext);
    if (!context) {
        throw new Error('useCalendar must be used within CalendarProvider');
    }
    return context;
};

export const CalendarProvider = ({ children }) => {
    // View state
    const [viewMode, setViewMode] = useState(VIEW_MODES.WEEK);
    const [currentDate, setCurrentDate] = useState(() => dayjs());
    const [zoomLevel, setZoomLevel] = useState(CALENDAR_CONSTANTS.DEFAULT_ZOOM_LEVEL);

    // Selection state
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedTimeRange, setSelectedTimeRange] = useState(null);
    const [hoveredEvent, setHoveredEvent] = useState(null);

    // UI state
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [draggedEvent, setDraggedEvent] = useState(null);
    const [showAvailability, setShowAvailability] = useState(true);

    // Filter state
    const [filterTrainerId, setFilterTrainerId] = useState(null);
    const [filterServiceId, setFilterServiceId] = useState(null);
    const [showPending, setShowPending] = useState(true);
    const [showConfirmed, setShowConfirmed] = useState(true);
    const [showLocked, setShowLocked] = useState(true);
    const [showGroups, setShowGroups] = useState(true);

    // Preferences
    const [use24HourFormat, setUse24HourFormat] = useState(true);
    const [snapToGrid, setSnapToGrid] = useState(true);
    const [snapMinutes, setSnapMinutes] = useState(CALENDAR_CONSTANTS.GRID_SNAP_MINUTES);

    // Navigation functions
    const goToToday = useCallback(() => {
        setCurrentDate(dayjs());
    }, []);

    const goToPreviousWeek = useCallback(() => {
        setCurrentDate(prev => prev.subtract(1, 'week'));
    }, []);

    const goToNextWeek = useCallback(() => {
        setCurrentDate(prev => prev.add(1, 'week'));
    }, []);

    const goToDate = useCallback((date) => {
        setCurrentDate(dayjs(date));
    }, []);

    // Zoom functions
    const zoomIn = useCallback(() => {
        setZoomLevel(prev => Math.min(prev + 0.25, CALENDAR_CONSTANTS.MAX_ZOOM_LEVEL));
    }, []);

    const zoomOut = useCallback(() => {
        setZoomLevel(prev => Math.max(prev - 0.25, CALENDAR_CONSTANTS.MIN_ZOOM_LEVEL));
    }, []);

    const resetZoom = useCallback(() => {
        setZoomLevel(CALENDAR_CONSTANTS.DEFAULT_ZOOM_LEVEL);
    }, []);

    // Get computed values
    const getHourHeight = useCallback(() => {
        return CALENDAR_CONSTANTS.HOUR_HEIGHT * zoomLevel;
    }, [zoomLevel]);

    const getWeekStart = useCallback(() => {
        return dayjs(currentDate).startOf('isoWeek');
    }, [currentDate]);

    const getWeekEnd = useCallback(() => {
        return dayjs(currentDate).endOf('isoWeek');
    }, [currentDate]);

    // Event selection
    const selectEvent = useCallback((event) => {
        setSelectedEvent(event);
        setSelectedTimeRange(null);
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedEvent(null);
        setSelectedTimeRange(null);
    }, []);

    // Time range selection
    const selectTimeRange = useCallback((start, end) => {
        setSelectedTimeRange({ start, end });
        setSelectedEvent(null);
    }, []);

    // Filter functions
    const isEventVisible = useCallback((event) => {
        // Check trainer filter
        if (filterTrainerId && event.trainerId !== filterTrainerId) {
            return false;
        }

        // Check service filter
        if (filterServiceId && event.serviceId !== filterServiceId) {
            return false;
        }

        // Check type filters
        if (!showPending && !event.confirmed) return false;
        if (!showConfirmed && event.confirmed) return false;
        if (!showLocked && event.isLocked) return false;
        if (!showGroups && event.trainingGroup) return false;

        return true;
    }, [filterTrainerId, filterServiceId, showPending, showConfirmed, showLocked, showGroups]);

    // Drag and drop state management
    const startDragging = useCallback((event) => {
        setIsDragging(true);
        setDraggedEvent(event);
    }, []);

    const stopDragging = useCallback(() => {
        setIsDragging(false);
        setDraggedEvent(null);
    }, []);

    const startResizing = useCallback((event) => {
        setIsResizing(true);
        setDraggedEvent(event);
    }, []);

    const stopResizing = useCallback(() => {
        setIsResizing(false);
        setDraggedEvent(null);
    }, []);

    const value = {
        // State
        viewMode,
        currentDate,
        zoomLevel,
        selectedEvent,
        selectedTimeRange,
        hoveredEvent,
        isDragging,
        isResizing,
        draggedEvent,
        showAvailability,
        filterTrainerId,
        filterServiceId,
        showPending,
        showConfirmed,
        showLocked,
        showGroups,
        use24HourFormat,
        snapToGrid,
        snapMinutes,

        // Setters
        setViewMode,
        setCurrentDate,
        setZoomLevel,
        setSelectedEvent,
        setSelectedTimeRange,
        setHoveredEvent,
        setShowAvailability,
        setFilterTrainerId,
        setFilterServiceId,
        setShowPending,
        setShowConfirmed,
        setShowLocked,
        setShowGroups,
        setUse24HourFormat,
        setSnapToGrid,
        setSnapMinutes,

        // Navigation
        goToToday,
        goToPreviousWeek,
        goToNextWeek,
        goToDate,

        // Zoom
        zoomIn,
        zoomOut,
        resetZoom,

        // Computed
        getHourHeight,
        getWeekStart,
        getWeekEnd,

        // Selection
        selectEvent,
        clearSelection,
        selectTimeRange,

        // Filters
        isEventVisible,

        // Drag and drop
        startDragging,
        stopDragging,
        startResizing,
        stopResizing,
    };

    return (
        <CalendarContext.Provider value={value}>
            {children}
        </CalendarContext.Provider>
    );
};